/**
 * Central settings for the smart AI (AI-05, AI-08). Everything tunable lives here.
 *
 * Units used across the AI:
 *   - points: the game's own score units (position + hand value + contig).
 *   - U (utility): estimated probability of winning the MATCH (0..1), see matchUtil.ts. Every action
 *     and state choice is compared in U, so points never get added to probabilities directly.
 */

export interface Budget {
  /** Sampled worlds per decision, spent over the rounds below (successive halving). */
  worlds: number;
  /** Fraction of `worlds` used in each round, and how many candidates survive into the next round. */
  rounds: { share: number; keep: number }[];
  /** Hard stop for one decision; the best action evaluated so far is used. */
  maxMs: number;
  /** Most further turns played out in one simulated hand (the rest is scored as it stands). */
  horizon: number;
  /** Browser: hand control back to the page this often (ms). 0 = never (headless benchmark). */
  yieldEveryMs: number;
}

export const BUDGETS: Record<"live" | "bench" | "fast" | "test", Budget> = {
  // the table already pauses ~0.9 s per AI move, so a few hundred ms of thinking is not noticed
  live: { worlds: 64, rounds: [{ share: 0.2, keep: 10 }, { share: 0.3, keep: 4 }, { share: 0.5, keep: 4 }], maxMs: 450, horizon: 30, yieldEveryMs: 12 },
  bench: { worlds: 40, rounds: [{ share: 0.2, keep: 8 }, { share: 0.3, keep: 3 }, { share: 0.5, keep: 3 }], maxMs: 400, horizon: 30, yieldEveryMs: 0 },
  // large benchmark batches: same policy, fewer samples, and no clock (a fixed sample count makes
  // every match reproducible from its seed, whatever the machine load)
  fast: { worlds: 24, rounds: [{ share: 0.25, keep: 6 }, { share: 0.25, keep: 3 }, { share: 0.5, keep: 3 }], maxMs: Infinity, horizon: 30, yieldEveryMs: 0 },
  // deterministic: no time limit, so a fixed seed always gives the same answer
  test: { worlds: 48, rounds: [{ share: 0.25, keep: 10 }, { share: 0.25, keep: 4 }, { share: 0.5, keep: 4 }], maxMs: Infinity, horizon: 30, yieldEveryMs: 0 },
};

export type ProfileName = "cautious" | "aggressive" | "opportunistic" | "observant";

/**
 * Personality never overrides clear evidence: a profile only chooses among the actions whose
 * estimated U is statistically indistinguishable from the best one (within `nearZ` standard errors).
 * Inside that set it applies its own taste.
 */
export interface Profile {
  /** A personality (cautious …) or a persona id (personas.ts). */
  name: ProfileName | string;
  /** + prefers outcomes that vary less, − prefers bigger swings (per unit of U's spread across worlds). */
  riskPreference: number;
  /** Extra liking for knocking / for taking the whole middle, in units of the near-best margin. */
  knockBias: number;
  takeMiddleBias: number;
  /** Random tie-break among near-equal options (seeded), in units of the near-best margin. */
  exploration: number;
  /** Use "probably still holds it" card tracking and the opponents' observed knocking habits. */
  useInference: boolean;
  /** Width of the near-best set. */
  nearZ: number;
}

export const PROFILES: Record<ProfileName, Profile> = {
  cautious:      { name: "cautious",      riskPreference: -1.0, knockBias: -0.8, takeMiddleBias: -0.8, exploration: 0.2, useInference: false, nearZ: 1.0 },
  aggressive:    { name: "aggressive",    riskPreference: 0.5,  knockBias: 0.8,  takeMiddleBias: 0.2,  exploration: 0.3, useInference: false, nearZ: 1.0 },
  opportunistic: { name: "opportunistic", riskPreference: 0.3,  knockBias: -0.2, takeMiddleBias: 1.0,  exploration: 0.3, useInference: false, nearZ: 1.0 },
  observant:     { name: "observant",     riskPreference: 0,    knockBias: 0,    takeMiddleBias: 0,    exploration: 0.2, useInference: true,  nearZ: 1.0 },
};

export const PROFILE_ORDER: ProfileName[] = ["cautious", "aggressive", "opportunistic", "observant"];

/** Match model used to turn points into a win probability (calibrated from baseline self-play). */
export const MATCH_MODEL = {
  /** Standard deviation of the points difference between two players over one hand. */
  sdPerHand: 5.45, // measured: 300 baseline self-play matches, per-hand sd 5.47, 50-hand final gap sd 38.5 (= 5.45 × √50)
  /** Quasi-random draws for the other players' futures (fixed, so results are reproducible). */
  draws: 48,
};

/** Developer diagnostics: set `globalThis.__POLI_AI_DEBUG = true` (browser console or Node). */
export const debugOn = (): boolean => !!(globalThis as { __POLI_AI_DEBUG?: boolean }).__POLI_AI_DEBUG;
